// DataWashPage — the two-pane DataWash interface (Phase 3A).
//
// LEFT: CaseSidebar (case list). RIGHT: CaseDetailPane. Holds the active
// case + new-case-modal state. Replaces the old routed SPA (Dashboard ->
// Cases table -> CaseDetail).

import { useState, useRef, useCallback, useEffect } from 'react';
import { LogOut } from 'lucide-react';
import { useAuthStore } from '../stores/auth';
import { useNavigate } from 'react-router-dom';
import CaseSidebar from './CaseSidebar';
import CaseDetailPane from './CaseDetailPane';
import NewCaseModal from './NewCaseModal';
import ToastContainer from './Toast';
import { useToastStore } from '../stores/toast';
import type { Case } from '../api/cases';

export default function DataWashPage() {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const toast = useToastStore();
  const [activeCase, setActiveCase] = useState<Case | null>(null);
  const [activeTab, setActiveTab] = useState<string>('chat');
  const [showNewCase, setShowNewCase] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sidebarRefreshKey, setSidebarRefreshKey] = useState(0);

  // --- Resizable sidebar ---
  const SIDEBAR_MIN = 200;
  const SIDEBAR_MAX = 520;
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const draggingRef = useRef(false);

  const onResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (!draggingRef.current) return;
      const newWidth = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, e.clientX));
      setSidebarWidth(newWidth);
    };
    const onMouseUp = () => {
      if (!draggingRef.current) return;
      draggingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNewCase = () => setShowNewCase(true);

  const handleCaseCreated = (c: Case) => {
    setShowNewCase(false);
    setActiveCase(c);
    setSidebarRefreshKey((k) => k + 1);
    toast.addToast('Case created');
  };

  const handleSelectCase = (c: Case) => {
    setActiveCase(c);
  };

  const handleDocumentsChanged = () => {
    setRefreshKey((k) => k + 1);
    setSidebarRefreshKey((k) => k + 1);
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-slate-50">
      {/* Minimal top bar (user + logout) */}
      <header className="bg-white border-b border-slate-200 flex-shrink-0 z-30 h-10">
        <div className="flex items-center justify-between h-full px-4">
          <div className="text-xs text-slate-500 hidden sm:block">
            {user?.full_name}
          </div>
          <button
            onClick={handleLogout}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            aria-label="Logout"
            title="Logout"
          >
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Two-pane body */}
      <div className="flex flex-1 overflow-hidden">
        <CaseSidebar
          activeCaseId={activeCase?.id || null}
          onSelectCase={handleSelectCase}
          onNewCase={handleNewCase}
          refreshKey={sidebarRefreshKey}
          width={sidebarWidth}
        />
        {/* Resize handle */}
        <div
          onMouseDown={onResizeStart}
          className="w-1 flex-shrink-0 bg-slate-200 hover:bg-primary-400 active:bg-primary-500 cursor-col-resize transition-colors z-20"
          data-testid="sidebar-resize-handle"
        />
        {activeCase ? (
          <CaseDetailPane
            caseSummary={activeCase}
            refreshKey={refreshKey}
            onDocumentsChanged={handleDocumentsChanged}
            activeTab={activeTab}
            onTabChange={setActiveTab}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-slate-400" data-testid="empty-pane">
            <div className="text-center">
              <div className="text-sm">Select a case from the sidebar, or create a new one to get started.</div>
            </div>
          </div>
        )}
      </div>

      {showNewCase && (
        <NewCaseModal
          onClose={() => setShowNewCase(false)}
          onCreated={handleCaseCreated}
        />
      )}
      <ToastContainer />
    </div>
  );
}